import { toZonedTime } from 'date-fns-tz';
import { memo, useMemo } from 'react';

import { buildMonthGrid, isSameDayInTimeZone } from '../../utils/dateGrid';
import MonthCell from '../MonthCell';

import type { MonthViewProps } from './types';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function isSameMonthInTimeZone(a: Date, b: Date, timeZone: string): boolean {
  const zonedA = toZonedTime(a, timeZone);
  const zonedB = toZonedTime(b, timeZone);
  return zonedA.getFullYear() === zonedB.getFullYear() && zonedA.getMonth() === zonedB.getMonth();
}

function MonthView({
  date,
  events,
  timeZone,
  isDateHighlighted,
  onEventClick,
  onSlotClick,
  onDayNumberClick,
  renderEventContent,
  renderEventPopover
}: MonthViewProps) {
  const grid = useMemo(() => buildMonthGrid(date, timeZone), [date, timeZone]);
  const now = useMemo(() => new Date(), []);

  const weeks = useMemo(() => {
    const result: Date[][] = [];
    for (let i = 0; i < grid.length; i += 7) result.push(grid.slice(i, i + 7));
    return result;
  }, [grid]);

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-7">
        {WEEKDAY_LABELS.map((label) => (
          <div
            key={label}
            className="border-b border-l border-uniform-gray-100 py-1 text-center text-xs font-medium text-uniform-gray-600 first:border-l-0 dark:border-uniform-gray-800 dark:text-uniform-gray-300">
            {label}
          </div>
        ))}
      </div>
      <div className="grid flex-1 grid-cols-7 border-r border-uniform-gray-100 dark:border-uniform-gray-800">
        {weeks.flatMap((week) =>
          week.map((day) => (
            <MonthCell
              key={day.toISOString()}
              day={day}
              events={events}
              timeZone={timeZone}
              isCurrentMonth={isSameMonthInTimeZone(day, date, timeZone)}
              isHighlighted={isDateHighlighted?.(day)}
              isToday={isSameDayInTimeZone(day, now, timeZone)}
              onEventClick={onEventClick}
              onSlotClick={onSlotClick}
              onDayNumberClick={onDayNumberClick}
              renderEventContent={renderEventContent}
              renderEventPopover={renderEventPopover}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default memo(MonthView);
